class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    this.heroTarget = null;
    this.searchTrigger = null;
    this.searchModal = null;
    this.searchPanel = null;
    this.searchInput = null;
    this.menuTrigger = null;
    this.menuDrawer = null;
    this.menuPanel = null;
    this.openPanelTrigger = null;
    this.mobileHomepageQuery = window.matchMedia('(max-width: 47.9375rem)');
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.syncHeroTextColor = this.syncHeroTextColor.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.section-header');
    this.stickyType = this.dataset.stickyType || 'always';
    this.isHomepage = this.dataset.isHomepage === 'true';
    this.isArticlePage = this.dataset.isArticlePage === 'true';
    this.heroTarget = document.querySelector(this.dataset.heroSelector || '.hero-vermellio');
    this.syncHeroTextColor();

    this.searchTriggers = this.querySelectorAll('[data-open-search]:not(.vermeillo-header__drawer-search-link)');
    this.searchModal = this.querySelector('[data-search-modal]');
    this.searchPanel = this.searchModal?.querySelector('.vermeillo-header__search-panel');
    this.searchInput = this.searchModal?.querySelector('input[type="search"]');

    this.menuTrigger = this.querySelector('[data-open-menu]');
    this.menuDrawer = this.querySelector('[data-menu-drawer]');
    this.menuPanel = this.menuDrawer?.querySelector('.vermeillo-header__drawer-panel');

    this.searchTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.openSearch(trigger));
    });

    this.querySelectorAll('[data-close-search]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.closeSearch());
    });

    this.menuTrigger?.addEventListener('click', () => this.openMenu(this.menuTrigger));

    this.querySelectorAll('[data-close-menu]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.closeMenu());
    });

    this.menuDrawer?.querySelectorAll('[data-open-search]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.closeMenu(false);
        this.openSearch(trigger);
      });
    });

    document.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.setHeaderHeight();
    this.updateHeroState();
    this.handleScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    this.setHeaderHeight();
    this.syncHeroTextColor();
    this.updateHeroState();
  }

  handleKeyUp(event) {
    if (event.code !== 'Escape') return;

    if (this.searchModal && !this.searchModal.hasAttribute('hidden')) {
      this.closeSearch();
      return;
    }

    if (this.menuDrawer && !this.menuDrawer.hasAttribute('hidden')) {
      this.closeMenu();
    }
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    this.setHeaderHeight();
    this.updateHeroState();

    if (this.searchIsOpen() || this.menuIsOpen()) {
      this.section?.classList.add('shopify-section-header-sticky', 'menu-open');
      this.currentScrollTop = scrollTop;
      return;
    }

    this.section?.classList.remove('menu-open');

    if (this.stickyType === 'none') {
      this.section?.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
      this.currentScrollTop = scrollTop;
      return;
    }

    this.section?.classList.add('shopify-section-header-sticky');
    this.section?.classList.remove('shopify-section-header-hidden');
    this.currentScrollTop = scrollTop;
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  syncHeroTextColor() {
    const heroTextColor = this.heroTarget
      ? getComputedStyle(this.heroTarget).getPropertyValue('--hero-text-color').trim()
      : '';

    if (heroTextColor) {
      this.style.setProperty('--hero-text-color', heroTextColor);
      return;
    }

    this.style.removeProperty('--hero-text-color');
  }

  updateHeroState() {
    let isHeroState = false;
    let isArticleHeroState = false;

    if (this.heroTarget && this.isHomepage) {
      const heroBottom = this.heroTarget.getBoundingClientRect().bottom;

      isHeroState = this.mobileHomepageQuery.matches ? false : heroBottom > this.offsetHeight;
    }

    if (this.heroTarget && this.isArticlePage) {
      const heroBottom = this.heroTarget.getBoundingClientRect().bottom;
      isArticleHeroState = heroBottom > this.offsetHeight;
    }

    this.classList.toggle('is-hero-state', isHeroState);
    this.classList.toggle('is-article-hero-state', isArticleHeroState);
    this.classList.toggle('is-normal-state', !isHeroState && !isArticleHeroState);
  }

  openSearch(trigger) {
    if (!this.searchModal || !this.searchPanel) return;

    this.closeMenu(false);
    this.openPanelTrigger = trigger;
    this.searchModal.hidden = false;
    this.searchTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    this.section?.classList.add('shopify-section-header-sticky', 'menu-open');
    this.syncBodyLock();

    requestAnimationFrame(() => {
      trapFocus(this.searchPanel, this.searchInput || this.searchPanel);
      this.searchInput?.focus();
    });
  }

  closeSearch(restoreFocus = true) {
    if (!this.searchModal || this.searchModal.hasAttribute('hidden')) return;

    this.searchModal.hidden = true;
    this.searchTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    removeTrapFocus(restoreFocus ? this.openPanelTrigger : null);
    this.syncBodyLock();
  }

  openMenu(trigger) {
    if (!this.menuDrawer || !this.menuPanel) return;

    this.closeSearch(false);
    this.openPanelTrigger = trigger;
    this.menuDrawer.hidden = false;
    this.menuTrigger?.setAttribute('aria-expanded', 'true');
    this.section?.classList.add('shopify-section-header-sticky', 'menu-open');
    this.syncBodyLock();

    requestAnimationFrame(() => {
      trapFocus(this.menuPanel, this.menuPanel.querySelector('a, button, summary') || this.menuPanel);
    });
  }

  closeMenu(restoreFocus = true) {
    if (!this.menuDrawer || this.menuDrawer.hasAttribute('hidden')) return;

    this.menuDrawer.hidden = true;
    this.menuTrigger?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(restoreFocus ? this.openPanelTrigger : null);
    this.syncBodyLock();
  }

  searchIsOpen() {
    return Boolean(this.searchModal && !this.searchModal.hasAttribute('hidden'));
  }

  menuIsOpen() {
    return Boolean(this.menuDrawer && !this.menuDrawer.hasAttribute('hidden'));
  }

  syncBodyLock() {
    const cartDrawerIsActive = Boolean(document.querySelector('cart-drawer.active'));
    const shouldLock = this.searchIsOpen() || this.menuIsOpen() || cartDrawerIsActive;

    document.body.classList.toggle('overflow-hidden', shouldLock);

    if (!shouldLock) {
      this.section?.classList.remove('menu-open');
    }
  }

  hide() {
    this.section?.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
  }

  reveal() {
    this.section?.classList.add('shopify-section-header-sticky', 'animate');
    this.section?.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.section?.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
